import { useState } from 'react';

import { ModalWrapper, Typography } from '@braskai/ui-kit';

import { Close } from '@mui/icons-material';
import { Box, IconButton, Modal, Stack } from '@mui/material';

import { useTrialPricingModalStore } from '@/entities/pricing';

import { useExecuteOneTime } from '@/shared/hooks/useExecuteOneTime';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { PaymentInterval, Plan, Price } from '@/shared/types/Billing';

import { usePricingCardsController } from '../../model/usePricingCardsController';
import { BillingPeriodTabs } from '../BillingPeriodTabs';
import { PricingCard } from '../PricingCard';
import { EnterpriseBanner } from './EnterpriseBanner';

const TrialPricingModalComponent = () => {
  const close = useTrialPricingModalStore((state) => state.close);
  const [chosenPlanId, setChosenPlanId] = useState<null | string>(null);

  // Trial users default to monthly (consistent with the pricing page); the toggle switches to annual.
  const { paymentPeriod, plansGroupByCategory, yearlySelected, handleBuyPlanButtonClick, handleToggle } =
    usePricingCardsController({ defaultPaymentPeriod: PaymentInterval.Month, source: 'trialModal', setChosenPlanId });

  useExecuteOneTime(() => {
    MixpanelEvents.trialPricingModalShown();
  });

  const handleClose = () => {
    MixpanelEvents.trialPricingModalDismissed();
    close();
  };

  // Trial-specific plan-click event; the underlying checkout flow keeps its own (shared) events.
  const handlePlanGetStarted = (priceId: Price['id'], plan: Plan) => {
    MixpanelEvents.trialPricingModalPlanGetStartedClick({ period: paymentPeriod, plan: plan.category });
    handleBuyPlanButtonClick(priceId, plan);
  };

  return (
    <Modal
      sx={{ alignItems: 'flex-end', display: 'flex', justifyContent: 'center' }}
      open
      onClose={handleClose}
    >
      <Stack
        sx={{
          backgroundColor: 'common.white',
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          maxHeight: 'calc(100dvh - 40px)',
          overflowY: 'auto',
          position: 'relative',
          width: 'calc(100vw - 80px)',
        }}
      >
        <Stack alignItems="center" gap={4} paddingBottom={25} paddingTop={24} paddingX={24}>
          <Typography color="grey.900" variant="title-4-md">
            Unlock full access
          </Typography>
          <BillingPeriodTabs yearlySelected={yearlySelected} onToggle={handleToggle} />
        </Stack>
        <IconButton
          aria-label="Close"
          sx={{ color: 'grey.500', position: 'absolute', right: 24, top: 24 }}
          onClick={handleClose}
        >
          <Close sx={{ fontSize: 24 }} />
        </IconButton>

        <Stack gap={20} paddingBottom={200} paddingX={24}>
          <Box
            sx={{
              // Each card is at least 400px tall per the design (cards are the direct grid children).
              '& > *': { minHeight: 400 },
              display: 'grid',
              gap: 12,
              gridTemplateColumns: { laptop: 'repeat(3, 1fr)', xs: '1fr' },
            }}
          >
            {plansGroupByCategory.map((plansByCategory) => (
              <PricingCard
                key={plansByCategory.category}
                chosenPlanId={chosenPlanId}
                plansByCategory={plansByCategory}
                selectedPaymentPeriod={paymentPeriod}
                onActionButtonClick={handlePlanGetStarted}
              />
            ))}
          </Box>

          <EnterpriseBanner />
        </Stack>
      </Stack>
    </Modal>
  );
};

export const TrialPricingModal = () => (
  <ModalWrapper hook={useTrialPricingModalStore}>
    <TrialPricingModalComponent />
  </ModalWrapper>
);
